import React from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InputField from './ui/InputField';
import { PRICE_ADJUSTMENT_REASON_MAX_LENGTH } from '../utils';

/**
 * The "why was this price changed" field, shown next to a price adjustment.
 *
 * One component rather than four inline copies: room create, venue create, room
 * edit and venue edit all ask the same question about the same column
 * (`reservation.price_adjustment_reason`), and #1942 is precisely about that
 * column meaning one thing everywhere. Four copies is how the create and edit
 * screens drifted apart on the cap in the first place.
 *
 * SUP, storage and the other non-room bookings ask it too (#3332 D3): there the
 * reason lands on the inline adjustment voucher (`voucher.creation_reason`),
 * which the price adjustment list reads alongside the reservation column.
 *
 * The field is optional by design — a booking is never blocked on a reason
 * (`docs/architecture/reservation-price-adjustments.md`) — and it renders only
 * when there is an adjustment to explain, so a normal booking never sees it.
 */
interface PriceAdjustmentReasonFieldProps {
    /** The current adjustment. The field appears only when this is non-zero. */
    discount: number;
    value: string;
    onChange: (value: string) => void;
    /** Edit screens disable the whole form on a cancelled booking. */
    disabled?: boolean;
}

const PriceAdjustmentReasonField: React.FC<PriceAdjustmentReasonFieldProps> = ({
    discount,
    value,
    onChange,
    disabled,
}) => {
    const { t } = useTranslation();

    if (discount === 0) return null;

    return (
        <Grid item xs={12} className={'mt-2'}>
            <InputField
                label={t('common.price_adjustment_reason')}
                value={value}
                onChange={onChange}
                disabled={disabled}
                // The width of `reservation.price_adjustment_reason`. Capping
                // here means an over-long reason cannot reach the API at all,
                // rather than failing the write once the booking is submitted.
                maxLength={PRICE_ADJUSTMENT_REASON_MAX_LENGTH}
            />
        </Grid>
    );
};

export default PriceAdjustmentReasonField;
